import { LoaderCircle, FileUp } from 'lucide-react';
import { ElementRef, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { FileItem } from './FileItem';

import { useGetCsvs } from '@/hooks/useGetCsvs';
import type { CSVFile, CSVQueryFilterDto } from '@/services/CSVService';

interface FileListProps {
  onDownloadFile: (file: CSVFile) => void;
  generatingFileId?: string | null;
  downloadUrls?: Record<string, string>;
  queryFilterDto?: Partial<CSVQueryFilterDto>;
}

export function FileList({
  onDownloadFile,
  generatingFileId,
  downloadUrls = {},
  queryFilterDto,
}: FileListProps) {
  const { t: tCommon } = useTranslation('translation', {
    keyPrefix: 'pages.Settings.components.ProductsManager.components.FileList',
  });
  const { t } = useTranslation('translation', {
    keyPrefix: `pages.Settings.components.ProductsManager.${queryFilterDto?.type == 'SHOP_THE_LOOK' ? 'SHOP_THE_LOOK' : 'PRODUCT'}.components.FileList`,
  });

  const endFilesRef = useRef<ElementRef<'div'>>(null);

  const { csvs, isFetching, isFetchingNextPage, hasNextPage, fetchNextPage } =
    useGetCsvs(queryFilterDto);

  useEffect(() => {
    if (!endFilesRef.current) {
      return;
    }

    const observer = new IntersectionObserver(
      (entries, obs) => {
        const { isIntersecting } = entries[0];

        if (!hasNextPage) {
          obs.disconnect();
          return;
        }

        if (isIntersecting && !isFetchingNextPage) {
          fetchNextPage();
        }
      },
      {
        rootMargin: '30px',
      },
    );

    observer.observe(endFilesRef.current);

    return () => {
      observer.disconnect();
    };
  }, [isFetchingNextPage, hasNextPage, fetchNextPage]);

  if (csvs.length === 0 && !isFetching) {
    return (
      <div className="py-12 text-center">
        <div className="mx-auto mb-6 flex h-24 w-24 items-center justify-center rounded-full bg-muted">
          <FileUp className="h-10 w-10 text-muted-foreground" />
        </div>
        <h3 className="mb-2 text-lg font-semibold text-foreground">
          {tCommon('noFilesTitle')}
        </h3>
        <p className="mx-auto mb-6 max-w-sm text-sm text-muted-foreground">
          {t('noFilesDescription')}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {csvs.map((file) => (
        <FileItem
          key={file.id}
          file={file}
          onDownloadFile={onDownloadFile}
          isGenerating={generatingFileId === file.id}
          hasDownloadUrl={!!downloadUrls[file.id]}
        />
      ))}

      <div ref={endFilesRef} className="py-4">
        {isFetching && (
          <div className="flex justify-center">
            <LoaderCircle className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        )}
      </div>
    </div>
  );
}
